/**
 * 容园线格式 ↔ 星闪 UI 键码（与 KEYCODE_MAP / EXTRA_KEYS_MATRIX 同一命名空间）
 *
 * 华奋达、贝盈改键源盘均使用星闪键码（如 8374、62208）；容园读写设备仍为 XD 四字节，
 * 在 UI 层统一转换为星闪码，以便 ICON_CONFIG、i18n comm、getKeyCodeInfo 复用。
 */

import type { RongyuanXdKeyData } from "./rongyuanKeyCodes";

export const RONGYUAN_UI_DISABLED = 0x00ffffff;

/** 星闪多媒体：0x2000 + (param3 << 8) + consumerValue */
function xsConsumerCode(value: number, param3: number): number {
  return 0x2000 + (param3 << 8) + (value & 0xff);
}

function parseXsConsumer(uiCode: number): RongyuanXdKeyData | null {
  if (uiCode < 0x2000 || uiCode >= 0x2300) return null;
  const param3 = ((uiCode >> 8) & 0xff) - 0x20;
  if (param3 < 0 || param3 > 2) return null;
  return { page: 0x03, param1: 0, value: uiCode & 0xff, param2: param3 };
}

/** 星闪鼠标按键：0x4000 + (hid - 0xef) * 0x100 */
function xsMouseButtonCode(hid: number): number {
  return 0x4000 + (hid - 0xef) * 0x100;
}

function parseXsMouseButton(uiCode: number): RongyuanXdKeyData | null {
  if (uiCode < 0x4000 || uiCode >= 0x5000) return null;
  if ((uiCode & 0xff) !== 0) return null;
  const hid = 0xef + (uiCode - 0x4000) / 0x100;
  if (hid < 0xf0 || hid > 0xf8) return null;
  return { page: 0x01, param1: 0, value: hid & 0xff, param2: 0 };
}

/** 星闪 Profile：61957–61960 对应 Profile+ / - / 循环 / 切换（value 1–4） */
function xsProfileCode(value: number, param2: number): number | null {
  if (param2 !== 0) return null;
  if (value === 1) return 61957;
  if (value === 2) return 61958;
  if (value === 3) return 61959;
  if (value === 4) return 61960;
  return null;
}

function parseXsProfile(uiCode: number): RongyuanXdKeyData | null {
  switch (uiCode) {
    case 61957:
      return { page: 0x08, param1: 0, value: 0x01, param2: 0 };
    case 61958:
      return { page: 0x08, param1: 0, value: 0x02, param2: 0 };
    case 61959:
      return { page: 0x08, param1: 0, value: 0x03, param2: 0 };
    case 61960:
      return { page: 0x08, param1: 0, value: 0x04, param2: 0 };
    default:
      return null;
  }
}

/** 线格式签名 → 星闪 UI 码（显式表，覆盖公式无法表达的项） */
const XD_SIG_TO_XS = new Map<string, number>([
  // 系统 / 灯效 / 回报率
  ["10:2:0:0", 61952],
  ["10:9:0:0", 62231],
  ["10:12:0:0", 62215],
  ["10:3:0:0", 61961],
  ["10:8:0:0", 62471],
  ["13:1:0:0", 62208],
  ["13:8:3:2", 62208],
  ["13:8:3:0", 62225],
  ["13:8:0:0", 62224],
  ["13:8:4:0", 62214],
  ["13:8:4:1", 62215],
  ["13:2:1:0", 62210],
  ["13:2:2:0", 62211],
  ["13:3:1:0", 62212],
  ["13:3:2:0", 62213],
  ["13:5:1:0", 62209],
  ["5:0:1:0", 62228],
  ["5:0:2:0", 62229],
  // 滚轮滚动
  ["1:0:245:1", 18945],
  ["1:0:245:255", 19201],
  // 鼠标平移
  ["1:0:246:5", 18176],
  ["1:0:246:251", 17920],
  ["1:0:247:5", 18688],
  ["1:0:247:251", 18432],
  // 邮件（KEYCODE_MAP 中注释掉，星闪码仍可用）
  ["3:0:138:1", 8586],
  // 禁用
  ["0:0:0:0", RONGYUAN_UI_DISABLED],
]);

/** 星闪 UI 码 → 线格式（显式表） */
const XS_TO_XD = new Map<number, RongyuanXdKeyData>([
  [61952, { page: 0x0a, param1: 0x02, value: 0x00, param2: 0x00 }],
  [62231, { page: 0x0a, param1: 0x09, value: 0x00, param2: 0x00 }],
  [62209, { page: 0x0d, param1: 0x05, value: 0x01, param2: 0x00 }],
  [62210, { page: 0x0d, param1: 0x02, value: 0x01, param2: 0x00 }],
  [62211, { page: 0x0d, param1: 0x02, value: 0x02, param2: 0x00 }],
  [62212, { page: 0x0d, param1: 0x03, value: 0x01, param2: 0x00 }],
  [62213, { page: 0x0d, param1: 0x03, value: 0x02, param2: 0x00 }],
  [61961, { page: 0x0a, param1: 0x03, value: 0x00, param2: 0x00 }],
  [62471, { page: 0x0a, param1: 0x08, value: 0x00, param2: 0x00 }],
  [62208, { page: 0x0d, param1: 0x01, value: 0x00, param2: 0x00 }],
  [62214, { page: 0x0d, param1: 0x08, value: 0x04, param2: 0x00 }],
  [62215, { page: 0x0a, param1: 0x0c, value: 0x00, param2: 0x00 }],
  [62224, { page: 0x0d, param1: 0x08, value: 0x00, param2: 0x00 }],
  [62225, { page: 0x0d, param1: 0x08, value: 0x03, param2: 0x00 }],
  [62228, { page: 0x05, param1: 0x00, value: 0x01, param2: 0x00 }],
  [62229, { page: 0x05, param1: 0x00, value: 0x02, param2: 0x00 }],
  [18945, { page: 0x01, param1: 0, value: 0xf5, param2: 0x01 }],
  [19201, { page: 0x01, param1: 0, value: 0xf5, param2: 0xff }],
  [18176, { page: 0x01, param1: 0, value: 0xf6, param2: 0x05 }],
  [17920, { page: 0x01, param1: 0, value: 0xf6, param2: 0xfb }],
  [18688, { page: 0x01, param1: 0, value: 0xf7, param2: 0x05 }],
  [18432, { page: 0x01, param1: 0, value: 0xf7, param2: 0xfb }],
  [8586, { page: 0x03, param1: 0, value: 0x8a, param2: 0x01 }],
  [61957, { page: 0x08, param1: 0, value: 0x01, param2: 0 }],
  [61958, { page: 0x08, param1: 0, value: 0x02, param2: 0 }],
  [61959, { page: 0x08, param1: 0, value: 0x03, param2: 0 }],
  [61960, { page: 0x08, param1: 0, value: 0x04, param2: 0 }],
  [RONGYUAN_UI_DISABLED, { page: 0, param1: 0, value: 0, param2: 0 }],
]);

function xdSignature(xd: RongyuanXdKeyData): string {
  return `${xd.page}:${xd.param1}:${xd.value}:${xd.param2}`;
}

function packRongyuanFallback(xd: RongyuanXdKeyData): number {
  if (xd.page === 0 && xd.param1 === 0 && xd.param2 === 0 && xd.value <= 0xff) {
    return xd.value;
  }
  return (
    ((xd.page & 0xff) << 24) |
    ((xd.param1 & 0xff) << 16) |
    ((xd.value & 0xff) << 8) |
    (xd.param2 & 0xff)
  );
}

/** XD 线格式 → 星闪 UI 键码（设备读回 / 写盘前展示） */
export function rongyuanXdToXsUiCode(
  page: number,
  param1: number,
  value: number,
  param2: number
): number {
  const xd: RongyuanXdKeyData = { page, param1, value, param2 };
  const sig = xdSignature(xd);
  const explicit = XD_SIG_TO_XS.get(sig);
  if (explicit !== undefined) return explicit;

  if (page === 0) {
    if (param1 === 0 && param2 === 0) return value & 0xff;
    return packRongyuanFallback(xd);
  }

  if (page === 0x03 && param1 === 0) {
    return xsConsumerCode(value, param2);
  }

  if (page === 0x01 && param1 === 0) {
    if (value >= 0xf0 && value <= 0xf4 && param2 === 0) {
      return xsMouseButtonCode(value);
    }
    if (value >= 0xf5 && value <= 0xf8 && param2 === 0) {
      return xsMouseButtonCode(value);
    }
  }

  if (page === 0x08 && param1 === 0) {
    const profileXs = xsProfileCode(value, param2);
    if (profileXs !== null) return profileXs;
  }

  return packRongyuanFallback(xd);
}

function unpackPage0ComboUiCode(uiCode: number): RongyuanXdKeyData | null {
  if (uiCode <= 0xff || uiCode >= 0x01000000) return null;
  // 星闪命名空间保留段，勿当作 page=0 组合键解析
  if (uiCode >= 0x2000 && uiCode < 0x5000) return null;
  if (uiCode >= 61696 && uiCode <= 61699) return null;
  if (uiCode >= 61952 && uiCode <= 61961) return null;
  if (uiCode >= 62720 && uiCode <= 62735) return null;
  return {
    page: 0,
    param1: (uiCode >>> 16) & 0xff,
    value: (uiCode >>> 8) & 0xff,
    param2: uiCode & 0xff,
  };
}

/** 星闪 UI 键码 → XD 线格式（改键写盘） */
export function xsUiCodeToRongyuanXd(uiCode: number): RongyuanXdKeyData | null {
  if (uiCode === RONGYUAN_UI_DISABLED) {
    return { page: 0, param1: 0, value: 0, param2: 0 };
  }

  const explicit = XS_TO_XD.get(uiCode);
  if (explicit) return { ...explicit };

  const consumer = parseXsConsumer(uiCode);
  if (consumer) return consumer;

  const mouseBtn = parseXsMouseButton(uiCode);
  if (mouseBtn) return mouseBtn;

  const profile = parseXsProfile(uiCode);
  if (profile) return profile;

  const page0Combo = unpackPage0ComboUiCode(uiCode);
  if (page0Combo) return page0Combo;

  if (uiCode >= 0x01000000) {
    return {
      page: (uiCode >>> 24) & 0xff,
      param1: (uiCode >>> 16) & 0xff,
      value: (uiCode >>> 8) & 0xff,
      param2: uiCode & 0xff,
    };
  }

  if (uiCode <= 0xff) {
    return { page: 0, param1: 0, value: uiCode, param2: 0 };
  }

  return null;
}

/** 注册协议扩展键的线格式 → UI 码（构建元数据表时调用） */
export function registerRongyuanXdToXsMapping(
  xd: RongyuanXdKeyData,
  xsUiCode: number
): void {
  XD_SIG_TO_XS.set(xdSignature(xd), xsUiCode);
  XS_TO_XD.set(xsUiCode, { ...xd });
}

/** 是否为 page≠0 或四字节完整打包（非单字节 HID） */
export function isRongyuanPackedUiCode(uiCode: number): boolean {
  return uiCode > 0xff;
}
